/**
 * Premium shop / black market state (Gold spend catalog —
 * `backend/src/game/shop/`). Mirrors `vendor-state.ts`'s `createStore` +
 * fetch pattern.
 *
 * Unlike `vendor-state.ts`, this does NOT track its own `balance` field kept
 * live by a scoped `economy:updated` subscription: `hud.ts` already
 * subscribes to that event globally (for the persistent Cash/Gold readout)
 * and writes into `gameStateStore`'s `character.softBalance`/
 * `.premiumBalance`, so this store reads that directly instead of
 * duplicating the subscription. `buyShopItem`'s response balance is used
 * only for the immediate receipt line, same as `vendor-state.ts`'s
 * `lastReceipt` pattern.
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import { gameStateStore } from './game-state';
import type { InventorySheet } from './game-state.types';
import type {
  ShopState, ShopCatalogEntry, ShopHideoutSlotOffer, ShopHideoutSlotResult, ShopWindowState,
  ShopHelpingHandOffer, ShopHelpingHandResult,
} from './shop-state.types';

interface ShopBuyResponse {
  sheet: InventorySheet;
  balance: { cash: number; gold: number };
  goldSpent: number;
  itemKey: string;
  added: number;
  /** The row's post-purchase window state, so a capped card goes straight onto its cooldown. */
  window: ShopWindowState | null;
}

const INITIAL_STATE: ShopState = {
  storefront: 'MARKET',
  catalog: null,
  balance: null,
  hideoutSlots: null,
  helpingHand: null,
  tab: null,
  busy: false,
  error: null,
  lastReceipt: null,
};

export const shopStateStore = createStore<ShopState>({ ...INITIAL_STATE });

/**
 * The catalog and the hideout-space offer, in parallel and with SEPARATE
 * failures. They are two endpoints and one is not a catalog row (it delivers no
 * item — see `ShopHideoutSlotOffer`); a 500 on the offer must not blank the
 * shop, and vice versa.
 */
export async function loadShopCatalog(): Promise<void> {
  shopStateStore.set((s) => ({ ...s, busy: true, error: null }));
  const [catalog, slots, helpingHand] = await Promise.all([
    api.get<ShopCatalogEntry[]>('/shop/premium/catalog').catch((err: unknown) => err as Error),
    api.get<ShopHideoutSlotOffer>('/shop/premium/hideout-slots').catch(() => null),
    api.get<ShopHelpingHandOffer>('/shop/premium/helping-hand').catch(() => null),
  ]);
  if (catalog instanceof Error) {
    shopStateStore.set((s) => ({
      ...s, busy: false, error: msg(catalog, 'Couldn\'t load the shop catalog.'),
    }));
    return;
  }
  shopStateStore.set((s) => ({ ...s, catalog, hideoutSlots: slots, helpingHand, busy: false }));
}

/** Black Market or Atelier Hollis. */
export function setShopStorefront(storefront: ShopState['storefront']): void {
  shopStateStore.set((s) => ({ ...s, storefront, error: null, lastReceipt: null }));
}

/** Which tab the card grid is showing. Null re-resolves to the first category. */
export function setShopTab(tab: string | null): void {
  shopStateStore.set((s) => ({ ...s, tab }));
}

/** Buys one bundle of `key`. Delivers to HIDEOUT (server-validated space, no charge on rejection). */
export async function buyShopItem(key: string): Promise<void> {
  shopStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const result = await api.post<ShopBuyResponse>(`/shop/premium/${key}/buy`);
    gameStateStore.set((s) => ({ ...s, inventory: result.sheet }));
    shopStateStore.set((s) => ({
      ...s,
      busy: false,
      balance: result.balance,
      // Patched in place rather than re-fetching the whole catalog: the server
      // just told us this row's new allowance, and a refetch would repaint
      // every card to change one.
      catalog: s.catalog?.map((e) => (e.key === key ? { ...e, window: result.window } : e)) ?? null,
      lastReceipt: { message: `Bought ${result.added} for ${result.goldSpent} Gold.` },
    }));
  } catch (err) {
    shopStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t complete the purchase.') }));
  }
}

/**
 * Buys one step of hideout space.
 *
 * The response carries the fresh sheet as well as the new offer, and both are
 * written: the slot COUNTER the player is looking at lives on the inventory
 * sheet (`hideoutSlotCap`), not on the offer, so writing only the offer would
 * leave the shop saying 44 slots while the bag still said 40.
 */
export async function buyHideoutSlots(): Promise<void> {
  shopStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const result = await api.post<ShopHideoutSlotResult>('/shop/premium/hideout-slots');
    gameStateStore.set((s) => ({ ...s, inventory: result.sheet }));
    shopStateStore.set((s) => ({
      ...s,
      busy: false,
      balance: result.balance,
      hideoutSlots: result.offer,
      lastReceipt: {
        message: `Cleared space for ${result.slotsAdded} more for ${result.goldSpent} Gold.`,
      },
    }));
  } catch (err) {
    shopStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t buy the space.') }));
  }
}

/**
 * Buys Helping Hand (10h of 50% faster crafting).
 *
 * Writes the response's offer straight over the store's, so the card flips to
 * its active state at once. The Workshop's own `bench.helpingHandUntil` is
 * refreshed by the caller (`loadHome`), which this store does not import.
 */
export async function buyHelpingHand(): Promise<void> {
  shopStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const result = await api.post<ShopHelpingHandResult>('/shop/premium/helping-hand');
    shopStateStore.set((s) => ({
      ...s,
      busy: false,
      balance: result.balance,
      helpingHand: result.offer,
      lastReceipt: { message: `Helping Hand is running for ${result.goldSpent} Gold.` },
    }));
  } catch (err) {
    shopStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t buy Helping Hand.') }));
  }
}

export function resetShopState(): void {
  shopStateStore.set({ ...INITIAL_STATE });
}

function msg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
